import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

import MainContainer from "../../../components/utils/MainContainer";
import Seo from "../../../components/utils/Seo";
import { COUNTRY_CONFIG, PICKUP_INFO } from "../../../utils/constants";

const RS = COUNTRY_CONFIG.RS;
const ME = COUNTRY_CONFIG.ME;
const rsd = (n) => new Intl.NumberFormat("sr-RS").format(n);
/* "2–4 radna dana (PostExpres)" -> "2–4 radna dana" */
const rok = (c) => c.deliveryEstimate.replace(/\s*\(.*\)\s*$/, "");

/*
 * Pravne stranice (placeholder sadržaj dok pravni tekstovi ne budu spremni):
 * /uslovi-kupovine · /politika-privatnosti · /reklamacije
 */

const PAGES = {
  "/uslovi-kupovine": {
    label: "Pravno",
    title: "Uslovi kupovine",
    sections: [
      {
        heading: "Opšte odredbe",
        text: "Ovi uslovi kupovine definišu pravila naručivanja, plaćanja, isporuke i povraćaja proizvoda kupljenih preko МРЧ online prodavnice. Poručivanjem proizvoda prihvatate ove uslove u celosti.",
      },
      {
        heading: "Cene i plaćanje",
        text: "Sve cene su iskazane u dinarima (RSD). Plaćanje se vrši pouzećem prilikom preuzimanja pošiljke, i za Srbiju i za Crnu Goru i Bosnu i Hercegovinu.",
      },
      {
        heading: "Isporuka",
        text: `Isporuka se vrši PostExpres službom. Za Srbiju je rok ${rok(RS)} od potvrde porudžbine, poštarina je ${rsd(RS.deliveryPrice)} RSD po paketu, a preko ${rsd(RS.freeShippingThreshold)} RSD je besplatna. Šaljemo i u Crnu Goru i Bosnu i Hercegovinu, sa rokom od ${rok(ME)} i poštarinom od ${ME.deliveryPriceEur} €. Lično preuzimanje je moguće u ${PICKUP_INFO.cityLocative} ${PICKUP_INFO.hours.replace("–", " do ")}, a tačnu adresu dobijaš u mejlu sa potvrdom porudžbine.`,
      },
      {
        heading: "Povraćaj i zamena",
        text: "Kupac ima pravo na zamenu ili povraćaj proizvoda u roku od 30 dana od prijema, pod uslovom da proizvod nije korišćen i da je u originalnom stanju.",
      },
    ],
  },
  "/politika-privatnosti": {
    label: "Pravno",
    title: "Politika privatnosti",
    sections: [
      {
        heading: "Podaci koje prikupljamo",
        text: "Prilikom kupovine prikupljamo isključivo podatke neophodne za obradu porudžbine: ime i prezime, adresu za isporuku, broj telefona i email adresu.",
      },
      {
        heading: "Kako koristimo podatke",
        text: "Podaci se koriste isključivo za obradu i isporuku porudžbina i komunikaciju u vezi sa njima. Podatke ne delimo sa trećim licima, osim kurirskoj službi radi isporuke.",
      },
      {
        heading: "Newsletter",
        text: "Email adrese prijavljene na newsletter koriste se samo za slanje obaveštenja o novim proizvodima i akcijama. Odjava je moguća u svakom trenutku putem linka u poruci.",
      },
      {
        heading: "Vaša prava",
        text: "U svakom trenutku možete zatražiti uvid, izmenu ili brisanje svojih podataka slanjem zahteva na info@mrc.rs.",
      },
    ],
  },
  "/reklamacije": {
    label: "Pravno",
    title: "Reklamacije",
    sections: [
      {
        heading: "Kako podneti reklamaciju",
        text: "Reklamaciju možete podneti slanjem email-a na info@mrc.rs sa brojem porudžbine, opisom problema i fotografijom proizvoda ukoliko je oštećen.",
      },
      {
        heading: "Rok za reklamaciju",
        text: "Reklamacije na oštećenja nastala u transportu primaju se u roku od 24h od prijema pošiljke. Reklamacije na skrivene nedostatke primaju se u zakonskom roku od 2 godine.",
      },
      {
        heading: "Rešavanje reklamacije",
        text: "Na reklamaciju odgovaramo u roku od 8 dana, a rešavamo je u roku od 15 dana od prijema. Osnovana reklamacija se rešava zamenom proizvoda ili povraćajem novca.",
      },
    ],
  },
};

const LegalPage = () => {
  const { pathname } = useLocation();
  const page = PAGES[pathname] || PAGES["/uslovi-kupovine"];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <MainContainer>
      {/* SEO, naslov po ruti (Uslovi kupovine / Politika privatnosti / Reklamacije) */}
      <Seo title={page.title} canonical={pathname} />
      <Wrap key={pathname}>
        <SectionLabel>{page.label}</SectionLabel>
        <Title>{page.title}</Title>

        {page.sections.map((section) => (
          <Section key={section.heading}>
            <h2>{section.heading}</h2>
            <p>{section.text}</p>
          </Section>
        ))}

        <Note>
          Za sva dodatna pitanja pišite nam na{" "}
          <a href="mailto:info@mrc.rs">info@mrc.rs</a>.
        </Note>
      </Wrap>
    </MainContainer>
  );
};

export default LegalPage;

/* ------------------------------- stilovi ------------------------------- */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const labelIn = keyframes`
  from { opacity: 0; letter-spacing: 0.45em; }
  to { opacity: 1; letter-spacing: 0.15em; }
`;

/* key={pathname} remount-uje sadržaj, pa ulazak ide i pri prelasku
   između pravnih stranica */
const Wrap = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 3rem 1.5rem 5rem;
  width: 100%;

  & > * {
    animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 8 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${i * 80}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

const SectionLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.75rem;
  animation: ${labelIn} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both !important;

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  text-transform: uppercase;
  letter-spacing: -0.01em;
  line-height: 0.95;
  color: var(--text-primary);
  margin: 0 0 2.5rem;
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 0.6rem;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.95rem;
    line-height: 1.7;
    margin: 0;
  }
`;

const Note = styled.p`
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
  color: var(--text-muted);
  font-size: 0.88rem;

  a {
    color: var(--brand);
  }
`;
